import React from 'react';

// A stale open tab requests JS chunks whose hashes changed on the last deploy;
// the dynamic import 404s and React throws before any UI mounts. Detect that
// specific case and hard-reload once (guarded so it can't loop) to pull the new
// bundle. Every other error renders an inline card instead of a white screen.
const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = error.name || '';
  const msg = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      const KEY = 'gn_chunk_reload_at';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      // Only auto-reload if we haven't already tried within the last 10s.
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isChunkLoadError(error)) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-slate-600 mb-4">A new version is available.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Reload
          </button>
        </div>
      );
    }

    return (
      <div className="m-6 rounded-xl border border-red-200 bg-red-50 p-6 max-w-2xl">
        <h3 className="font-semibold text-red-800">This section failed to load</h3>
        <p className="mt-1 text-sm text-red-700 break-words">
          {error.message || String(error)}
        </p>
        <button
          onClick={this.reset}
          className="mt-4 px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
